/**
 * Pure view helpers for the Users screen (f3.1).
 *
 * Everything here is a function of data the SERVER sent. In particular:
 *
 * * **No status is computed.** `status` arrives on the row. Deriving it from
 *   `is_active` and a role code in the browser is exactly how the two ends come
 *   to disagree -- and f3.2 adds an `invited` status that does NOT imply an
 *   inactive account, at which point a client-side derivation would be quietly
 *   wrong rather than loudly missing.
 * * **No role vocabulary.** Nothing here lists role codes. The picker is driven
 *   by f2.2's `PLATFORM_ROLE` catalogue at runtime, so a role inserted in phase
 *   10 or 16 renders with no change to this file (BR-9).
 *
 * The one presentational exception is `badgeClassFor`, and it is a two-entry map
 * with a default rather than a ladder -- an unrecognised code still renders.
 */

import type { UserAccount, UserStatus } from '@/api/client';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

/**
 * The lookup type f2.2 registered for platform roles. Not a role code.
 *
 * Defined in `@/users/platformRoles` since f8.3, because the import screen
 * needs it too and must not import from this screen's folder. Re-exported here
 * so the Users screen's own modules keep reading it from one place.
 */
export { PLATFORM_ROLE_TYPE } from '@/users/platformRoles';

/**
 * Which pill class a role badge wears.
 *
 * Two entries and a default, so a role this build has never heard of renders
 * as `pill-info` with whatever name the catalogue gave it, rather than
 * crashing or rendering unstyled. That default is what makes "a fourth role
 * needs no frontend change" true rather than aspirational.
 *
 * `PLATFORM_ADMIN` is called out because it is the one role whose holder can
 * administer everyone else; `UNASSIGNED` because it grants nothing and should
 * not look like a grant.
 */
const BADGE_BY_ROLE_CODE: Readonly<Record<string, string>> = {
  PLATFORM_ADMIN: 'pill-admin',
  UNASSIGNED: 'pill-neutral',
};

export function badgeClassFor(roleCode: string): string {
  return BADGE_BY_ROLE_CODE[roleCode] ?? 'pill-info';
}

/** How the server's status value is presented. Labels only -- never a rule. */
export const STATUS_PRESENTATION: Readonly<
  Record<UserStatus, { label: string; className: string }>
> = {
  active: { label: 'Active', className: 'pill-success' },
  awaiting_role: { label: 'Awaiting role', className: 'pill-warning' },
  deactivated: { label: 'Deactivated', className: 'pill-neutral' },
  // f3.2. An account an Admin created that nobody has signed in to yet --
  // outstanding rather than wrong, which is what `pill-pending` says.
  invited: { label: 'Invited', className: 'pill-pending' },
};

/**
 * A status the client does not recognise still renders.
 *
 * f3.2 adds `invited`, and a server that is one deploy ahead of the browser
 * must not produce a blank cell.
 */
export function statusPresentation(status: string) {
  return (
    STATUS_PRESENTATION[status as UserStatus] ?? {
      label: status.replace(/_/g, ' '),
      className: 'pill-neutral',
    }
  );
}

/** Up to two initials for the avatar, from whatever the display name is. */
export function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * The status control.
 *
 * It is a FILTER in the toolbar, not a tab strip. Status is one attribute of
 * an account among several, and every other attribute the roster filters on
 * is a control in the toolbar; promoting one of them to a row of tabs said
 * that status was a different kind of thing, which it is not.
 *
 * Options are the statuses actually PRESENT in the roster, in the order the
 * presentation map declares, followed by any status this build has never
 * heard of. A filter offering a status no account holds is a control that can
 * only ever empty the table.
 */
export interface StatusOption {
  readonly value: string;
  readonly label: string;
  readonly count: number;
}

export function statusOptions(users: readonly UserAccount[]): StatusOption[] {
  const counts = new Map<string, number>();
  for (const user of users) {
    counts.set(user.status, (counts.get(user.status) ?? 0) + 1);
  }

  const known = Object.keys(STATUS_PRESENTATION).filter((status) => counts.has(status));
  const unknown = [...counts.keys()].filter((status) => !(status in STATUS_PRESENTATION));

  return [...known, ...unknown].map((status) => ({
    value: status,
    label: statusPresentation(status).label,
    count: counts.get(status) ?? 0,
  }));
}

/** Case-insensitive match over the two columns the toolbar says it searches. */
export function matchesSearch(user: UserAccount, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    user.display_name.toLowerCase().includes(needle) ||
    user.email.toLowerCase().includes(needle)
  );
}

export interface UserFilters {
  readonly search: string;
  /** A role code from the catalogue, or empty for "All roles". */
  readonly roleCode: string;
  /** A status value, or empty for "All statuses". */
  readonly status: string;
}

/**
 * Search, role and status, all client-side over the loaded set and all three
 * the same kind of control.
 *
 * The server sent every row in display-name order and filtering preserves it,
 * so narrowing the list never reshuffles what is left. Ordering is the sort
 * model's job, applied afterwards.
 */
export function applyFilters(
  users: readonly UserAccount[],
  { search, roleCode, status }: UserFilters,
): UserAccount[] {
  return users.filter(
    (user) =>
      matchesSearch(user, search) &&
      (roleCode === '' || user.role.code === roleCode) &&
      (status === '' || user.status === status),
  );
}

/**
 * Which column the roster is sorted by. EVERY column the table renders is
 * here except the one holding the row actions.
 */
export type SortColumn = 'user' | 'email' | 'role' | 'lastSignIn' | 'status';

/** The default order: by user, ascending, which is how the server sends it. */
export const DEFAULT_SORT: SortState<SortColumn> = { column: 'user', direction: 'asc' };

/**
 * What a column sorts by for one account.
 *
 * The role and status columns sort by the LABEL the reader sees rather than
 * by the code behind it, and last sign-in sorts by the instant rather than by
 * the formatted string, so "31 Dec" does not file before "1 Jan".
 */
export function sortValueOf(user: UserAccount, column: SortColumn): SortValue {
  switch (column) {
    case 'user':
      return user.display_name;
    case 'email':
      return user.email;
    case 'role':
      return user.role.name;
    case 'status':
      return statusPresentation(user.status).label;
    case 'lastSignIn':
      return user.last_login_at ? new Date(user.last_login_at) : null;
  }
}

/** The roster in the reader's chosen order. Never sorted in place. */
export function orderedForDisplay(
  users: readonly UserAccount[],
  sort: SortState<SortColumn>,
): UserAccount[] {
  return sortRows(users, sort, sortValueOf);
}

/**
 * `20 Sep 2026, 08:14`, or the prototype's `never` where there is no value.
 *
 * Transported as UTC and shown in the READER'S zone, which is f1.5's house
 * rule (BR-F1.5-15): an Admin correlating a sign-in with a conversation
 * reasons in their own clock.
 *
 * The locale is pinned to `en-GB` rather than left to the reader, which is the
 * one place this diverges from f1.5's `formatLocalShort`. It is deliberate:
 * the prototype specifies `20 Sep 2026, 08:14`, and most readers here are
 * en-US, whose default would render `09/20/2026` and lose the format the
 * screen was designed around. The ZONE is the reader's; only the spelling is
 * fixed.
 */
export function formatSignIn(value: string | null | undefined): string | null {
  if (!value) return null;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(when);
}
